import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { logger } from '@core/helpers/logger';
import { ChangableComponent } from '@models/changable.component';
import { Store } from '@ngrx/store';
import { openMeetingRescheduleWindow } from '@store/actions/profile.actions';
import { addSeconds, differenceInMinutes } from 'date-fns';
import {
  BillingMeetingChargingEnum,
  ConfirmationOptions,
  EMeetingUserAction,
  IAccountStats,
  ImageSizeEnum,
  Meeting,
  MeetingTypeEnum,
  Router as ContentRouter,
  SubjectCategoryEnum,
  User,
} from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { OnUiCover } from 'onclass-ui/lib/modules/on-ui-cover/on-ui-cover.component';
import { takeUntil, tap } from 'rxjs/operators';
import {
  AccountService,
  AuthService,
  BillingV2Service,
  ContextService,
  FilesService,
  MeetingsService,
  RequestService,
} from '../../services';

type ConfirmationActionType = 'cancel' | 'leave';

interface IMeetingCardComponentState {
  copied: boolean;
  cut: boolean;
  subscribed: boolean;
  inviteModal: boolean;
  meetingFormModal: boolean;
  confirmationAlert: boolean;
  confirmationId: string;
  confirmationAction: ConfirmationActionType;
  confirmationOptions: ConfirmationOptions;
}

@Component({
  selector: 'app-meeting-card',
  templateUrl: './meeting-card.component.html',
  styleUrls: ['./meeting-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeetingCardComponent extends ChangableComponent implements OnInit {
  deviceService = inject(DeviceDetectorService);
  private accountService = inject(AccountService);
  private authService = inject(AuthService);
  private billingV2Service = inject(BillingV2Service);
  private meetingsService = inject(MeetingsService);
  private contextService = inject(ContextService);
  private router = inject(Router);
  private readonly store = inject(Store);
  private request = inject(RequestService);

  @Input() public set meeting(meeting: Meeting) {
    this._meeting = meeting instanceof Meeting ? meeting : new Meeting(meeting);
    this.state.cut = (this.meeting.description || '').length > this.cutMaxLength;
    this.setupCard();
  }
  public get meeting(): Meeting {
    return this._meeting;
  }
  @Input() modal: boolean;
  @Output() changed = new EventEmitter<boolean>();
  public me: User;
  public accountRoute;
  public meetingJoinRoute;
  public fullTitle: string;
  public constants = {
    disabled: BillingMeetingChargingEnum.disabled,
    credits: BillingMeetingChargingEnum.credits,
    fee: BillingMeetingChargingEnum.fee,
  };
  public cutMaxLength = 214;
  public state: IMeetingCardComponentState = {
    copied: false,
    cut: false,
    subscribed: false,
    inviteModal: false,
    meetingFormModal: false,
    confirmationAlert: false,
    confirmationId: null,
    confirmationAction: null,
    confirmationOptions: null,
  };
  public author: User;
  public authorFullName: string;
  public subject: string;
  public isNativeLanguage: boolean;
  public title: string;
  public level: string;
  public authorAvatar: OnUiCover;
  public cover: OnUiCover;
  public coverImageUrl: string;
  public meetingBeginAt: Date;
  public meetingEndAt: Date;
  public meetingUrl: string;
  public priceFormatted: string;
  public paymentFormVisible = false;
  public subjectCategoryEnum = SubjectCategoryEnum;
  public debug$ = this.contextService.debug$;
  public meetingTypeEnum = MeetingTypeEnum;
  public authorStats: Partial<IAccountStats>;
  public differenceInMinutes = differenceInMinutes;

  private _meeting: Meeting;

  public ngOnInit(): void {
    this.contextService.me$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (me) => {
        this.me = me;
        this.setupCard();
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      },
    });
  }

  private loadAuthorDetails(id: string) {
    if (!id) {
      return;
    }
    this.accountService
      .getUserById(id, 'LOAD_TIME')
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (user) => {
          this.author = user;
          this.accountRoute = AccountService.accountRoute(user);
          this.authorFullName = AccountService.getUserName(user);
          this.authorStats = user.stats;
          this.authorAvatar = {
            type: 'avatar',
            img: FilesService.getFileUrlBySize(user?.userpic_id, ImageSizeEnum.sm),
            title: this.authorFullName,
            link: this.accountRoute,
            form: 'circle',
          };
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  protected setupCard() {
    this.loadAuthorDetails(this.meeting.author_id);
    this.fullTitle = this.meetingsService.meetingFullTitle(this.meeting);
    this.cover = {
      type: 'cover',
      img:
        FilesService.getFileBySize(this.meeting?.cover, [ImageSizeEnum.wide, ImageSizeEnum.md])?.url ||
        FilesService.getFileUrlBySize(this.meeting.cover_id, ImageSizeEnum.wide),
      title: this.fullTitle,
      link: this.meetingJoinRoute,
      aspect: '16/9',
    };
    this.meetingBeginAt = new Date(this.meeting.begin_at);
    this.meetingEndAt = addSeconds(new Date(this.meeting.end_at), 1);
    this.meetingJoinRoute = ContentRouter.meetingJoinRoute(this.meeting);
    this.meetingUrl = this.request.host + this.router.createUrlTree(this.meetingJoinRoute).toString();
    this.subject = this.meeting.subject ? this.meeting.subject?.title : null;
    this.isNativeLanguage = this.meeting?.subject?.category_id === SubjectCategoryEnum.native_language;
    this.level = this.meeting.level ? this.meeting.level.title : 'subject-levels.any-level';
    this.title = this.meeting?.title;
    this.markForCheck();

    if (this.isPaidMeeting) {
      this.billingV2Service
        .formatAmount$(this.meeting.price_tier)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (priceTier) => {
            this.priceFormatted = priceTier;
            this.markForCheck();
          },
          error: (error) => {
            this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          },
        });
    }
  }

  public openPaymentForm() {
    this.paymentFormVisible = true;
    this.detectChanges();
  }

  public closePaymentForm() {
    this.paymentFormVisible = false;
    this.detectChanges();
  }

  public onPaymentSucceed() {
    this.closePaymentForm();
    this.changed.emit(true);
    this.detectChanges();
  }

  public can(action: EMeetingUserAction | string): boolean {
    if (!this._meeting || !this._meeting.can) {
      return false;
    }
    return !!this._meeting.can[action];
  }

  public get isGuest(): boolean {
    return this._meeting?.is?.guest;
  }

  public get isPaidMeeting(): boolean {
    return this._meeting?.is?.paidMeeting;
  }

  public authorize() {
    this.authService.showAuthModal(() => this.redirectToJoin());
  }

  private redirectToJoin() {
    void this.router.navigate(this.meetingJoinRoute, { queryParams: { join: 'auto' } });
  }

  public get description(): string {
    const desc = this.meeting?.description || '';

    return this.isBrowser && this.state.cut
      ? desc.length > this.cutMaxLength
        ? desc.substr(0, this.cutMaxLength) + '...'
        : desc
      : desc;
  }

  public get canCut(): boolean {
    const desc = this.meeting.description || '';
    return this.isBrowser && desc.length > this.cutMaxLength;
  }

  public toggleCut() {
    this.state.cut = !this.state.cut;
    this.markForCheck();
  }

  public onCopied(event) {
    this.state.copied = true;
    this.markForCheck();

    this.setTimeout(() => {
      this.state.copied = false;
      this.markForCheck();
    }, 1000);
  }

  public unsubscribe() {
    this.accountService
      .unsubscribe(this._meeting.author_id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.state.subscribed = false;
          this.markForCheck();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'SAVE-PROBLEM');
          this.state.subscribed = false;
          this.markForCheck();
        },
      });
  }

  public openInviteModal() {
    this.state.inviteModal = true;
    this.markForCheck();
  }

  public closeInviteModal() {
    this.state.inviteModal = false;
    this.markForCheck();
  }

  public leaveMeeting() {
    this.meetingsService
      .requestMeetingLeave(this._meeting.id)
      .pipe(tap(), takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          if (response.can) {
            this.state.confirmationAction = 'leave';
            this.state.confirmationId = response.confirmation_id;
            this.state.confirmationOptions = response.confirmation;
            this.openConfirmationAlert();
          } else {
            logger.warn("Can't leave meeting for now");
          }
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
        },
      });
  }

  public cancelMeeting() {
    this.meetingsService
      .requestMeetingCancel(this._meeting.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          if (response.can) {
            this.state.confirmationAction = 'cancel';
            this.state.confirmationId = response.confirmation_id;
            this.state.confirmationOptions = response.confirmation;
            this.openConfirmationAlert();
          } else {
            logger.warn("Can't cancel meeting for now");
          }
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
        },
      });
  }

  public openConfirmationAlert() {
    this.state.confirmationAlert = true;
    this.detectChanges();
  }

  public closeConfirmationAlert() {
    this.state.confirmationAlert = false;
    this.detectChanges();
  }

  public confirm() {
    switch (this.state.confirmationAction) {
      case 'leave':
        return this.leaveMeetingConfirm();

      case 'cancel':
        return this.cancelMeetingConfirm();
    }
  }

  public leaveMeetingConfirm() {
    this.meetingsService
      .confirmMeetingLeave(this._meeting.id, this.state.confirmationId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          this._meeting = response.model;
          this.setupCard();
          this.closeConfirmationAlert();
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
        },
      });
  }

  public cancelMeetingConfirm() {
    this.meetingsService
      .confirmMeetingCancel(this._meeting.id, this.state.confirmationId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          this._meeting = response.model;
          this.setupCard();
          this.closeConfirmationAlert();
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
        },
      });
  }

  public onReschedule() {
    this.store.dispatch(openMeetingRescheduleWindow({ meeting_id: this.meeting.id }));
  }

  public printMeetingInfo() {
    logger.info({
      meeting: this._meeting,
    });
  }

  public typeLabel(type: MeetingTypeEnum): string {
    switch (type) {
      case MeetingTypeEnum.single:
        return 'meetings.types.single';
      case MeetingTypeEnum.group:
        return 'meetings.types.group';
      case MeetingTypeEnum.webinar:
        return 'meetings.types.webinar';
      case MeetingTypeEnum.stream:
        return 'meetings.types.stream';
      case MeetingTypeEnum.talk:
        return 'meetings.types.talk';
      default:
        return '';
    }
  }

  public openMeetingFormModal() {
    this.state.meetingFormModal = true;
    this.detectChanges();
  }

  public closeMeetingFormModal() {
    this.state.meetingFormModal = false;
    this.detectChanges();
  }

  public onMeetingSaved() {
    this.closeMeetingFormModal();
    this.changed.emit(true);
  }

  public get isMe(): boolean {
    return this.me?.id === this.meeting?.author_id;
  }
}
